import React, { useMemo } from "react";
import makeStyles from "@mui/styles/makeStyles";
import useRoutes, { getLinkComponentAttributes } from "../../hooks/useRoutes";
import { Trans, useTranslation } from "react-i18next";

import { advertFormatCurrency } from "@/react/utils";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import Popup, {
  PopupActions,
  PopupButton,
  PopupContent,
  PopupHeadingContainer,
} from "../../../components/Common/Popup";
import PointBurst from "../PointBurst";
import Box from "@mui/material/Box";
import DigitalFileUpsaleIcon from "../../../components/Icons/DigitalFileUpsaleIcon";
import DigitalSetUpsaleIcon from "../../../components/Icons/DigitalSetUpsaleIcon";

const useStyles = makeStyles((theme) => ({
  header: {
    color: theme.palette.primary.dark,
    margin: 0,
    textAlign: "center",
  },
  popupHeaderContainer: {
    textAlign: "center",
    "& h2": {
      marginTop: 0,
      color: theme.palette.secondary.main,
      fontSize: "30px",
      fontWeight: 500,
      marginBottom: theme.spacing(1),
    },
    "& h3": {
      color: theme.palette.primary.dark,
      fontSize: "20px",
      fontWeight: 500,
      marginTop: 0,
      marginBottom: theme.spacing(1),
    },
  },
  singleFileRebatePrice: {
    fontSize: "20px",
  },
  singleFileRebateText: {
    fontSize: "12px",
    flex: 0,
  },
  singleFileRebateSaveText: {
    fontSize: "1rem",
  },
  productList: {
    display: "flex",
    justifyContent: "center",
    listStyle: "none",
    padding: 0,
    margin: 0,
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(2),
    "& > li": {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        marginRight: theme.spacing(6),
        marginLeft: theme.spacing(6),
      },
    },
  },
  popupContainer: {
    [theme.breakpoints.up("sm")]: {
      minWidth: "500px",
    },
    marginBottom: 0,
  },
  iconContainer: {
    width: "100%",
    position: "relative",
  },
  pointBurstContainer: {
    position: "absolute",
    top: "0px",
    right: "100px",
  },
  saveText: {
    color: theme.palette.secondary.main,
    textAlign: "center",
    fontSize: "30px",
  },
}));

const UpsaleDialogDigitalFiles = (props) => {
  return <DigitalFilesDialogue {...props} />;
};

export default UpsaleDialogDigitalFiles;

const DigitalFilesDialogue = ({ handleNextStep, digitalProducts }) => {
  const isMobile = useIsMobileDevice();
  const classes = useStyles();
  const routes = useRoutes();
  const { t } = useTranslation();

  const product = useMemo(() => {
    for (const product of digitalProducts) {
      if (product.type === "set") {
        return product;
      }
    }
    return digitalProducts[0];
  }, [digitalProducts]);

  return (
    <Popup
      open={true}
      fullWidth={isMobile}
      maxWidth="lg"
      data-testid="special-offer-dialog"
    >
      <PopupHeadingContainer className={classes.popupHeaderContainer}>
        <h2>
          <Trans>Special offer</Trans>
        </h2>
        <h3>
          {product.type === "set"
            ? t("Discounted digital portrait sets")
            : t("Discounted digital portrait files")}
        </h3>
      </PopupHeadingContainer>
      <PopupContent className={classes.popupContainer}>
        <ul className={classes.productList}>
          {product.type === "set" && (
            <li key={product.uuid}>
              <BuySetPointBurst product={product} />
            </li>
          )}
          {product.type === "file" && (
            <li key={product.uuid}>
              <BuyFilePointBurst product={product} />
            </li>
          )}
        </ul>
        <div className={classes.saveText}>
          {t("Save %s!", {
            postProcess: "sprintf",
            sprintf: [
              advertFormatCurrency(product.price - product.rebatedPrice),
            ],
          })}
        </div>
      </PopupContent>
      <PopupActions>
        <PopupButton
          onClick={handleNextStep}
          secondary
          data-testid="decline-button"
        >
          <Trans>No thanks</Trans>
        </PopupButton>
        <PopupButton
          {...getLinkComponentAttributes("products.digitalportraits", routes)}
        >
          <Trans>Check out offer</Trans>
        </PopupButton>
      </PopupActions>
    </Popup>
  );
};

const BuySetPointBurst = ({ product }) => {
  const classes = useStyles();

  return (
    <Box className={classes.iconContainer}>
      <div>
        <DigitalSetUpsaleIcon />
        <div className={classes.pointBurstContainer}>
          <PointBurst
            variant="secondary"
            content={
              <div>
                <div className={classes.singleFileRebatePrice}>
                  {advertFormatCurrency(product.rebatedPrice)}
                </div>
                <div className={classes.singleFileRebateText}>
                  <Trans>per set</Trans>
                </div>
              </div>
            }
          />
        </div>
      </div>
    </Box>
  );
};

const BuyFilePointBurst = ({ product }) => {
  const classes = useStyles();
  return (
    <Box className={classes.iconContainer}>
      <div>
        <DigitalFileUpsaleIcon />
        <div className={classes.pointBurstContainer}>
          <PointBurst
            variant="secondary"
            content={
              <div>
                <div className={classes.singleFileRebatePrice}>
                  {advertFormatCurrency(product.rebatedPrice)}
                </div>
                <div className={classes.singleFileRebateText}>
                  <Trans>per file</Trans>
                </div>
              </div>
            }
          />
        </div>
      </div>
    </Box>
  );
};
